import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useFlashMessage } from '../context/FlashMessageContext';
import { API_CONFIG } from '../config/api';
import { KeyRound, QrCode, Clock, Shield } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import './Dashboard.css';

function MyCredentials() {
  const { user, token, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useFlashMessage();
  
  // QR Code states
  const [authQR, setAuthQR] = useState(null);
  const [profileQR, setProfileQR] = useState(null);
  const [loadingAuthQR, setLoadingAuthQR] = useState(false);
  const [loadingProfileQR, setLoadingProfileQR] = useState(false);
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000));

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const generateAuthQR = useCallback(async () => {
    try {
      setLoadingAuthQR(true);
      showInfo('Generating authentication QR code...');
      
      const response = await axios.post(`${API_CONFIG.BASE_URL}/user/qr/auth`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAuthQR(response.data);
      showSuccess('Authentication QR code generated successfully!');
    } catch (error) {
      console.error('Failed to generate auth QR:', error);
      showError('Failed to generate authentication QR code. Please try again.');
    } finally {
      setLoadingAuthQR(false);
    }
  }, [token, showInfo, showSuccess, showError]);

  const generateProfileQR = useCallback(async () => {
    try {
      setLoadingProfileQR(true);
      // showInfo('Generating profile update QR code...');
      
      const response = await axios.post(`${API_CONFIG.BASE_URL}/user/qr/profile-update`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfileQR(response.data);
      // showSuccess('Profile update QR code generated successfully!');
    } catch (error) {
      console.error('Failed to generate profile QR:', error);
      showError('Failed to generate profile update QR code. Please try again.');
    } finally {
      setLoadingProfileQR(false);
    }
  }, [token, showError]);

  useEffect(() => {
    if (token && user) {
      generateAuthQR();
      generateProfileQR();
    }
  }, [token, user, generateAuthQR, generateProfileQR]);

  // Update timer every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const getTimeRemaining = (expiresAt) => {
    const remaining = expiresAt - currentTime;
    
    if (remaining <= 0) return 'Expired';
    
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    
    return `${minutes}m ${seconds}s`;
  };

  return (
    <DashboardLayout activeNav="credentials">
        <div className="dashboard-section">
          <h2>
            <QrCode size={24} />
            QR Code Generator
          </h2>
          <p>Generate secure QR codes for authentication and profile updates.</p>
          
          <div className="qr-generator-grid">
            {/* Authentication QR Code */}
            <div className="qr-generator-card">
              <div className="qr-card-header">
                <Shield size={24} />
                <h3>Authentication QR</h3>
                <p>For secure login and authentication</p>
              </div>
              
              <div className="qr-card-content">
                {loadingAuthQR ? (
                  <div className="qr-loading">
                    <div className="loading-spinner"></div>
                    <p>Generating authentication QR code...</p>
                  </div>
                ) : authQR ? (
                  <div className="qr-display">
                    <div className="qr-image-container">
                      <img 
                        src={authQR.qrCode} 
                        alt="Authentication QR Code"
                        className="qr-image"
                      />
                    </div>
                    <div className="qr-timer">
                      <Clock size={16} />
                      <span className={`time-remaining ${getTimeRemaining(authQR.qrData.expiresAt) === 'Expired' ? 'expired' : ''}`}>
                        {getTimeRemaining(authQR.qrData.expiresAt)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="qr-placeholder">
                    <QrCode size={48} />
                    <p>Failed to generate QR code</p>
                  </div>
                )}
              </div>
            </div>

            {/* Profile Update QR Code */}
            <div className="qr-generator-card hidden">
              <div className="qr-card-header">
                <KeyRound size={24} />
                <h3>Profile Update QR</h3>
                <p>For updating profile information</p>
              </div>
              
              <div className="qr-card-content hidden">
                {loadingProfileQR ? (
                  <div className="qr-loading">
                    <div className="loading-spinner"></div>
                    <p>Generating profile update QR code...</p>
                  </div>
                ) : profileQR ? (
                  <div className="qr-display">
                    <div className="qr-image-container">
                      <img 
                        src={profileQR.qrCode} 
                        alt="Profile Update QR Code"
                        className="qr-image"
                      />
                    </div>
                    <div className="qr-timer">
                      <Clock size={16} />
                      <span className={`time-remaining ${getTimeRemaining(profileQR.qrData.expiresAt) === 'Expired' ? 'expired' : ''}`}>
                        {getTimeRemaining(profileQR.qrData.expiresAt)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="qr-placeholder">
                    <QrCode size={48} />
                    <p>Failed to generate QR code</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
    </DashboardLayout>
  );
}

export default MyCredentials;